import { WeekState } from '@pickem/shared';

// Times are rendered in the brewery timezone. This mirrors the server default.
export const BREWERY_TZ = 'America/Boise';

export function formatKickoff(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BREWERY_TZ,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
}

export function formatKickoffTime(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BREWERY_TZ,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BREWERY_TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

/** Format a 10-digit string as (xxx) xxx-xxxx; pass through anything else. */
export function formatPhone(digits: string | null): string {
  if (!digits) return '';
  const d = digits.replace(/\D/g, '');
  if (d.length !== 10) return digits;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function ordinal(n: number | null): string {
  if (n == null) return '—';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const WEEK_STATE_LABEL: Record<WeekState, string> = {
  [WeekState.UPCOMING]: 'Upcoming',
  [WeekState.OPEN]: 'Open',
  [WeekState.LOCKED]: 'Locked',
  [WeekState.IN_PROGRESS]: 'In progress',
  [WeekState.COMPLETE]: 'Complete',
};

export function weekStateLabel(state: WeekState): string {
  return WEEK_STATE_LABEL[state] ?? state;
}

/**
 * Convert an ISO instant to a value for <input type="datetime-local">, in the
 * operator's *browser* local timezone (what the picker expects).
 */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

/** Convert a datetime-local value (browser local) back to an ISO UTC string. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Countdown-ish relative time until an ISO deadline (e.g. "in 3h 20m"). */
export function timeUntil(iso: string | null): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'closed';
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${m}m`;
  return `in ${m}m`;
}
